import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseMultiControl } from 'shared/abstract/base-multi-control';
import { ClickOutsideDirective } from 'shared/directives/click-outside.directive';
import { ListItem } from 'shared/interfaces/list-item';
import { HighlightPipe } from 'shared/pipes/highlight.pipe';
import { ModalService } from 'shared/services';
import { SharedModule } from 'shared/shared.module';

@Component({
    selector: 'app-multi-input-control',
    templateUrl: './multi-input-control.component.html',
    styleUrls: ['./multi-input-control.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiInputControlComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SharedModule, TranslateModule, NgClass, AsyncPipe, HighlightPipe, ClickOutsideDirective],
})
export class MultiInputControlComponent extends BaseMultiControl implements ControlValueAccessor, OnInit, OnDestroy {
    @Output() changeFull = new EventEmitter<ListItem[]>();

    constructor(modalService: ModalService, changeDetectorRef: ChangeDetectorRef, translate: TranslateService) {
        super(modalService, changeDetectorRef, translate);
    }

    public selectOption(option: ListItem, emitEvent = true, event: Event | null = null) {
        if (option) {
            this.selectedOptions.push(option);
            this.closeOptionsList();
            this.updateAvailableOptionsList();
            this.computeMaximumSelectionStatus();
            this.form.get('searchInput')?.setValue(undefined);

            if (emitEvent) {
                this.onChangeFunction(this.selectedOptions.map((opt) => opt.id));
                this.change.emit(this.selectedOptions.map((opt) => opt.id));
                this.changeFull.emit(this.selectedOptions);
                this.multiSelectInput?.first?.nativeElement?.focus();
                event?.preventDefault();
                event?.stopPropagation();
            }
        }
    }

    public onEnterKey(event: any) {
        event.preventDefault();
        event.stopPropagation();

        if (this.selectedIndex !== -1 && this.filteredAvailableOptionsList.length > this.selectedIndex) {
            if (this.isOptionsListVisible()) {
                if (this.selectedIndex === -1) {
                    this.closeOptionsList();
                    return;
                }

                const element = this.filteredAvailableOptionsList[this.selectedIndex];

                if (this.filteredAvailableOptionsList?.length > this.selectedIndex && !this.selectedOptions.includes(element)) {
                    this.selectOption(element);
                }
            } else {
                this.openOptionsList();
            }
        } else {
            const value = this.form.get('searchInput')!.value;

            if (value && !this.isOptionAlreadyPresent(value)) {
                this.selectOption(
                    new ListItem({
                        id: value,
                        label: value,
                    }),
                );
            }
        }
    }

    protected override handleExternalValueChanges(values: string[]) {
        this.selectedOptions = [];

        if (!this.internalOptionsList.length) {
            for (let i = 0; i < values.length; i++) {
                const value = values[i];

                this.selectOption(new ListItem(value.toString()));
            }
        }

        for (let i = 0; i < values.length; i++) {
            const value = values[i].toString();
            const index = this.internalOptionsList.findIndex((item) => item.id.toString() === value);
            this.selectOption(this.internalOptionsList[index], false);
        }

        this.externalOptionsQueue = undefined;
        // this.changeDetectorRef.detectChanges();
    }

    public addCustomOption() {
        const value = this.form.get('searchInput')!.value;

        if (!value || this.isOptionAlreadyPresent(value)) {
            return;
        }

        this.selectOption(
            new ListItem({
                id: value,
                label: value,
            }),
        );

        this.closeOptionsList();
    }

    public onBlur() {
        // The set timeout is required to delay the onTouched function call in case the user clicks the accept button
        // Timeout OK
        setTimeout(() => {
            this.onTouched();
        }, 50);
    }
}
